// v7.60 — Logística simplificada.
//
// REMOVIDO: cadastro manual de bairros um a um.
// MOTIVO: o delivery agora usa Google Maps para detectar o endereço, calcular
// a distância exata (Haversine) e aplicar a taxa por km. Qualquer endereço
// dentro do raio configurado é atendido automaticamente.
//
// MANTIDO: as 3 configurações que governam o cálculo:
//   - Raio de atendimento (km)
//   - Taxa-base (até 2km)
//   - Taxa por km acima de 2km
//   - Coordenadas do restaurante (referência do Haversine)

import { useState, useEffect } from 'react';
import db from '@/db';
import { formatCurrency } from '@/lib/formatters';
import { Input } from '@/components/ui/input';
import { MapPin, Save, Crosshair, Info, Calculator } from 'lucide-react';
import { toast } from 'sonner';
import LocationPicker from '@/components/LocationPicker';

const DEFAULTS = {
  restaurantLat: -22.6118426,
  restaurantLng: -43.1766153,
  deliveryRadiusKm: 8.0,
  deliveryBaseFee: 5.0,
  deliveryPerKm: 2.0,
};

export default function LogisticaPage() {
  const [form, setForm] = useState({ ...DEFAULTS });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [geoLoading, setGeoLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    const get = async (k, fallback) => (await db.config.get(k))?.value ?? fallback;
    setForm({
      restaurantLat: await get('restaurantLat', DEFAULTS.restaurantLat),
      restaurantLng: await get('restaurantLng', DEFAULTS.restaurantLng),
      deliveryRadiusKm: await get('deliveryRadiusKm', DEFAULTS.deliveryRadiusKm),
      deliveryBaseFee: await get('deliveryBaseFee', DEFAULTS.deliveryBaseFee),
      deliveryPerKm: await get('deliveryPerKm', DEFAULTS.deliveryPerKm),
    });
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const setField = (key, raw) => {
    const value = raw === '' ? '' : parseFloat(String(raw).replace(',', '.'));
    setForm((f) => ({ ...f, [key]: value }));
  };

  const useCurrentLocation = () => {
    if (!navigator.geolocation) {
      toast.error('Geolocalização não suportada neste dispositivo');
      return;
    }
    setGeoLoading(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setForm((f) => ({
          ...f,
          restaurantLat: parseFloat(pos.coords.latitude.toFixed(7)),
          restaurantLng: parseFloat(pos.coords.longitude.toFixed(7)),
        }));
        toast.success('Coordenadas atuais aplicadas!');
        setGeoLoading(false);
      },
      (err) => {
        toast.error(`Não foi possível obter localização: ${err.message}`);
        setGeoLoading(false);
      },
      { enableHighAccuracy: true, timeout: 8000 }
    );
  };

  const handleSave = async () => {
    // Validações
    if (!Number.isFinite(form.restaurantLat) || !Number.isFinite(form.restaurantLng)) {
      toast.error('Coordenadas do restaurante inválidas');
      return;
    }
    if (!Number.isFinite(form.deliveryRadiusKm) || form.deliveryRadiusKm <= 0) {
      toast.error('Raio de atendimento inválido');
      return;
    }
    if (!Number.isFinite(form.deliveryBaseFee) || form.deliveryBaseFee < 0) {
      toast.error('Taxa-base inválida');
      return;
    }
    if (!Number.isFinite(form.deliveryPerKm) || form.deliveryPerKm < 0) {
      toast.error('Taxa por km inválida');
      return;
    }
    setSaving(true);
    try {
      const put = (key, value) => db.config.put({ key, value });
      await Promise.all([
        put('restaurantLat', form.restaurantLat),
        put('restaurantLng', form.restaurantLng),
        put('deliveryRadiusKm', form.deliveryRadiusKm),
        put('deliveryBaseFee', form.deliveryBaseFee),
        put('deliveryPerKm', form.deliveryPerKm),
      ]);
      toast.success('Configurações de entrega salvas!');
    } catch (e) {
      toast.error(`Erro ao salvar: ${e.message || e}`);
    } finally {
      setSaving(false);
    }
  };

  // Simulador rápido — quanto fica a taxa para cada distância?
  const calcFee = (dist) => {
    if (!Number.isFinite(form.deliveryBaseFee) || !Number.isFinite(form.deliveryPerKm)) return 0;
    if (dist <= 2) return form.deliveryBaseFee;
    return form.deliveryBaseFee + (dist - 2) * form.deliveryPerKm;
  };

  const simulationRows = [1, 2, 3, 5, 7, 8].map((km) => ({
    km,
    fee: calcFee(km),
    inRange: km <= (form.deliveryRadiusKm || 0),
  }));

  if (loading) {
    return (
      <div className="p-6 lg:p-8 text-zinc-500">Carregando configurações...</div>
    );
  }

  return (
    <div data-testid="logistica-page" className="p-6 lg:p-8 max-w-4xl">
      <div className="mb-8">
        <h2 className="font-heading text-3xl tracking-tight font-bold text-zinc-900">Logística de Entrega</h2>
        <p className="text-sm text-zinc-500 mt-1">Raio de atendimento e taxa por km — totalmente automático via Google Maps</p>
      </div>

      <div className="mb-6 bg-blue-50 border border-blue-200 rounded-xl p-4 flex items-start gap-3">
        <Info size={18} className="text-blue-600 shrink-0 mt-0.5" />
        <div className="text-sm text-blue-900">
          <p className="font-semibold mb-1">Sem cadastro de bairros</p>
          <p className="text-xs leading-relaxed">
            Não é mais necessário registrar bairro por bairro. Qualquer endereço dentro do raio configurado
            abaixo é atendido automaticamente. O sistema usa o Google Maps para detectar a localização exata
            do cliente, mede a distância pelo Haversine (linha reta) e aplica <strong>Taxa-base</strong> até 2km +
            <strong> Taxa por km</strong> acima disso.
          </p>
        </div>
      </div>

      {/* Configurações principais */}
      <div className="bg-white border-2 border-zinc-200 rounded-2xl p-6 mb-6">
        <h3 className="font-heading text-lg font-bold text-zinc-900 mb-4 flex items-center gap-2">
          <Calculator size={18} className="text-[#2563eb]" /> Parâmetros de cobrança
        </h3>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="text-xs uppercase tracking-[0.15em] font-bold text-zinc-500 mb-1 block">Raio de atendimento (km)</label>
            <Input
              data-testid="delivery-radius-input"
              type="number"
              step="0.1"
              min="0.1"
              value={form.deliveryRadiusKm}
              onChange={(e) => setField('deliveryRadiusKm', e.target.value)}
              className="h-11 border-2 border-zinc-200 rounded-lg font-mono"
            />
            <p className="text-[10px] text-zinc-400 mt-1">Endereços além desse raio são recusados.</p>
          </div>
          <div>
            <label className="text-xs uppercase tracking-[0.15em] font-bold text-zinc-500 mb-1 block">Taxa-base (R$)</label>
            <Input
              data-testid="delivery-basefee-input"
              type="number"
              step="0.01"
              min="0"
              value={form.deliveryBaseFee}
              onChange={(e) => setField('deliveryBaseFee', e.target.value)}
              className="h-11 border-2 border-zinc-200 rounded-lg font-mono"
            />
            <p className="text-[10px] text-zinc-400 mt-1">Cobrada para distâncias até 2km.</p>
          </div>
          <div>
            <label className="text-xs uppercase tracking-[0.15em] font-bold text-zinc-500 mb-1 block">Taxa por km adicional (R$)</label>
            <Input
              data-testid="delivery-perkm-input"
              type="number"
              step="0.01"
              min="0"
              value={form.deliveryPerKm}
              onChange={(e) => setField('deliveryPerKm', e.target.value)}
              className="h-11 border-2 border-zinc-200 rounded-lg font-mono"
            />
            <p className="text-[10px] text-zinc-400 mt-1">Aplicada por km acima dos 2km iniciais.</p>
          </div>
        </div>

        {/* Simulador */}
        <div className="mt-6 pt-5 border-t border-zinc-100">
          <p className="text-xs font-bold uppercase tracking-[0.15em] text-zinc-500 mb-3">Simulação rápida</p>
          <div className="grid grid-cols-3 md:grid-cols-6 gap-2">
            {simulationRows.map((r) => (
              <div
                key={r.km}
                data-testid={`fee-sim-${r.km}km`}
                className={`rounded-lg p-3 text-center border-2 ${r.inRange ? 'border-emerald-200 bg-emerald-50' : 'border-zinc-200 bg-zinc-100 opacity-50'}`}
              >
                <p className="text-[10px] uppercase tracking-wider text-zinc-500">{r.km}km</p>
                <p className="font-mono font-bold text-zinc-900 mt-0.5">{r.inRange ? formatCurrency(r.fee) : '—'}</p>
              </div>
            ))}
          </div>
          <p className="text-[10px] text-zinc-400 mt-2">Distâncias fora do raio aparecem cinzas e bloqueadas.</p>
        </div>
      </div>

      {/* Coordenadas do restaurante */}
      <div className="bg-white border-2 border-zinc-200 rounded-2xl p-6 mb-6">
        <div className="flex items-start justify-between mb-4 gap-3 flex-wrap">
          <h3 className="font-heading text-lg font-bold text-zinc-900 flex items-center gap-2">
            <MapPin size={18} className="text-[#2563eb]" /> Coordenadas do restaurante
          </h3>
          <button
            data-testid="use-current-location-btn"
            type="button"
            onClick={useCurrentLocation}
            disabled={geoLoading}
            className="flex items-center gap-1.5 text-xs font-bold text-[#2563eb] hover:text-[#1d4ed8] disabled:opacity-50"
          >
            <Crosshair size={12} /> {geoLoading ? 'Localizando...' : 'Usar localização atual'}
          </button>
        </div>

        <LocationPicker
          lat={String(form.restaurantLat)}
          lng={String(form.restaurantLng)}
          onChange={({ lat, lng }) => setForm((f) => ({
            ...f,
            restaurantLat: parseFloat(lat.toFixed(7)),
            restaurantLng: parseFloat(lng.toFixed(7)),
          }))}
          height={300}
        />

        <div className="grid grid-cols-2 gap-3 mt-3">
          <div>
            <label className="text-xs uppercase tracking-[0.15em] font-bold text-zinc-500 mb-1 block">Latitude</label>
            <Input
              data-testid="restaurant-lat-input"
              value={form.restaurantLat}
              onChange={(e) => setField('restaurantLat', e.target.value)}
              className="h-11 border-2 border-zinc-200 rounded-lg font-mono"
            />
          </div>
          <div>
            <label className="text-xs uppercase tracking-[0.15em] font-bold text-zinc-500 mb-1 block">Longitude</label>
            <Input
              data-testid="restaurant-lng-input"
              value={form.restaurantLng}
              onChange={(e) => setField('restaurantLng', e.target.value)}
              className="h-11 border-2 border-zinc-200 rounded-lg font-mono"
            />
          </div>
        </div>
      </div>

      <div className="flex justify-end gap-3">
        <button
          data-testid="save-logistics-btn"
          onClick={handleSave}
          disabled={saving}
          className="flex items-center gap-2 bg-[#2563eb] text-white rounded-xl font-bold px-6 py-3 min-h-[48px] active:translate-y-[1px] transition-all hover:bg-[#1d4ed8] disabled:opacity-50"
        >
          <Save size={16} /> {saving ? 'Salvando...' : 'Salvar configurações'}
        </button>
      </div>
    </div>
  );
}
